'use client';

import VoiceSelector from '@/components/VoiceSelector';
import { useVapiCall } from '@/hooks/useVapiCall';
import { trackButtonClick } from '@/lib/analytics';
import { AIAgent } from '@/types/aiAgent';
import { ElevenLabsVoiceId } from '@/types/voice';
import { formatPhoneNumber } from '@/utils/phoneFormatter';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ClockIcon,
  ExclamationCircleIcon,
  GlobeAltIcon,
  MicrophoneIcon,
  PhoneIcon,
  SparklesIcon,
  StopCircleIcon,
} from '@heroicons/react/24/outline';
import Link from 'next/link';
import { Fragment, useEffect, useRef, useState } from 'react';

/**
 * Browser-window mockup shown during processing. Uses screenshot API to show
 * the site; width/height are derived from the placeholder so the image fits.
 */
function ProcessingBrowserMockup({ websiteUrl }: { websiteUrl: string }) {
  const contentRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | null>(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const { width, height } = entries[0]?.contentRect ?? {};
      if (width > 0 && height > 0) {
        setDimensions({ width: Math.round(width), height: Math.round(height) });
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Request at 2× display size for desktop-style preview; we render at half size below
  const query = new URLSearchParams({ url: websiteUrl });
  if (dimensions) {
    query.set('width', String(dimensions.width * 2));
    query.set('height', String(dimensions.height * 2));
  }
  const screenshotUrl = `/api/screenshot?${query.toString()}`;

  return (
    <div className="w-full h-[32rem] flex flex-col bg-white border border-gray-200 rounded-b overflow-hidden">
      {/* Browser chrome: URL bar */}
      <div className="flex-shrink-0 flex items-center gap-2 px-3 py-2 bg-gray-100 border-b border-gray-200">
        <div className="flex gap-1.5">
          <div className="w-3 h-3 rounded-full bg-red-400" />
          <div className="w-3 h-3 rounded-full bg-yellow-400" />
          <div className="w-3 h-3 rounded-full bg-green-400" />
        </div>
        <div className="flex-1 flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-200 rounded-md text-sm text-gray-500 truncate">
          <GlobeAltIcon className="h-4 w-4 flex-shrink-0 text-gray-400" aria-hidden />
          <span className="truncate">{websiteUrl}</span>
        </div>
      </div>
      {/* Content: screenshot sized to this placeholder */}
      <div ref={contentRef} className="relative flex-1 min-h-0 bg-gray-100 overflow-hidden">
        {dimensions && (
          <img
            src={screenshotUrl}
            alt=""
            width={dimensions.width}
            height={dimensions.height}
            className="max-w-full max-h-full object-cover object-top"
          />
        )}
        {/* Scanning overlay */}
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-600/90 text-white text-sm font-medium shadow-lg">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
            </span>
            Scanning website…
          </span>
          <div className="absolute inset-0">
            <div className="absolute left-0 right-0 top-0 h-1 bg-gradient-to-r from-transparent via-blue-400/50 to-transparent animate-scan" />
          </div>
        </div>
      </div>
    </div>
  );
}

const DEMO_STORAGE_KEY = 'greet_try_demo';

interface DemoAgent {
  id: string;
  extension: string;
  businessName: string;
  summary: string;
  prompt?: string;
  voiceModel: string;
  expiresInMinutes: number;
  createdAt: string;
  usedFallback?: boolean;
  vapiAgentId?: string;
}

function isDemoExpired(createdAt: string, expiresInMinutes: number): boolean {
  const expiresAt = new Date(createdAt).getTime() + expiresInMinutes * 60 * 1000;
  return Date.now() > expiresAt;
}

function loadDemoFromStorage(): DemoAgent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(DEMO_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as DemoAgent;
    if (!parsed.id || !parsed.extension || !parsed.createdAt) return null;
    if (isDemoExpired(parsed.createdAt, parsed.expiresInMinutes ?? 30)) {
      localStorage.removeItem(DEMO_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function saveDemoToStorage(demo: DemoAgent): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(demo));
  } catch {
    // ignore quota / private mode
  }
}

function clearDemoFromStorage(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(DEMO_STORAGE_KEY);
}

/** Format prompt for display: escape HTML, paragraphs, and **bold** */
function formatPromptForDisplay(text: string): string {
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
  const withBold = escaped.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  const paragraphs = withBold.split(/\n\n+/).filter((p) => p.trim());
  return paragraphs.map((p) => `<p class="mb-3 last:mb-0">${p.replace(/\n/g, '<br />')}</p>`).join('');
}

type PageState = 'input' | 'processing' | 'success' | 'error';

interface WebsiteDemoProps {
  /** Whether to show the processing browser mockup */
  showBrowserMockup?: boolean;
  /** Whether to show the voice selector in success state */
  showVoiceSelector?: boolean;
  /** Whether to show the advanced prompt section */
  showPromptSection?: boolean;
  /** Whether to show the CTA to sign up in success state */
  showSignupCTA?: boolean;
  /** Context for analytics tracking */
  analyticsContext?: string;
  /** Whether user is authenticated */
  isAuthenticated?: boolean;
  /** If true, creates a real agent instead of a demo agent */
  createRealAgent?: boolean;
  /** Callback when a real agent is created */
  onAgentCreated?: (agent: AIAgent) => void;
  /** Callback to skip to template selection */
  onSkipToTemplates?: () => void;
}

export default function WebsiteDemo({
  showBrowserMockup = true,
  showVoiceSelector = true,
  showPromptSection = true,
  showSignupCTA = true,
  analyticsContext = 'website_demo',
  isAuthenticated = false,
  createRealAgent = false,
  onAgentCreated,
  onSkipToTemplates,
}: WebsiteDemoProps) {
  const [pageState, setPageState] = useState<PageState>('input');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [urlError, setUrlError] = useState('');
  const [processingStep, setProcessingStep] = useState(0);
  const [demoAgent, setDemoAgent] = useState<DemoAgent | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorTitle, setErrorTitle] = useState('');
  const [remainingTime, setRemainingTime] = useState(30);
  const [showPromptExpanded, setShowPromptExpanded] = useState(false);

  const { callState, startCall, endCall } = useVapiCall();

  const demoPhone = (process.env.NEXT_PUBLIC_DEMO_PHONE_NUMBER || '').trim();

  const handleWebCall = async () => {
    if (!demoAgent?.vapiAgentId) return;
    await startCall(demoAgent.vapiAgentId);
  };

  const formatCallDuration = (seconds: number): string => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  // Restore demo from localStorage on mount (if still valid) - only for demo mode
  useEffect(() => {
    if (!createRealAgent) {
      const stored = loadDemoFromStorage();
      if (stored) {
        setDemoAgent(stored);
        setPageState('success');
      }
    }
  }, [createRealAgent]);

  // Collapse Advanced section when demo agent changes
  useEffect(() => {
    setShowPromptExpanded(false);
  }, [demoAgent?.id]);

  const processingSteps = [
    { label: 'Connecting to website...', shortLabel: 'Connecting', icon: GlobeAltIcon },
    { label: 'Analyzing business information...', shortLabel: 'Analyzing', icon: SparklesIcon },
    { label: 'Reading services and pricing...', shortLabel: 'Reading', icon: CheckCircleIcon },
    { label: 'Creating your AI agent...', shortLabel: 'Creating', icon: PhoneIcon },
  ];

  // Countdown timer for remaining time
  useEffect(() => {
    if (pageState !== 'success' || !demoAgent) return;

    const createdAt = new Date(demoAgent.createdAt);
    const expiresAt = new Date(createdAt.getTime() + demoAgent.expiresInMinutes * 60 * 1000);

    const updateRemainingTime = () => {
      const now = new Date();
      const remaining = Math.max(0, Math.floor((expiresAt.getTime() - now.getTime()) / 60000));
      setRemainingTime(remaining);

      if (remaining <= 0) {
        clearDemoFromStorage();
        setPageState('input');
        setDemoAgent(null);
      }
    };

    updateRemainingTime();
    const interval = setInterval(updateRemainingTime, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [pageState, demoAgent]);

  // Simulate processing steps
  useEffect(() => {
    if (pageState !== 'processing') return;

    const stepDurations = [2000, 8000, 8000, 4000];
    let currentStep = 0;

    const advanceStep = () => {
      currentStep++;
      if (currentStep < processingSteps.length) {
        setProcessingStep(currentStep);
        setTimeout(advanceStep, stepDurations[currentStep]);
      }
    };

    setTimeout(advanceStep, stepDurations[0]);
  }, [pageState, processingSteps.length]);

  const validateUrl = (url: string): boolean => {
    try {
      const parsed = new URL(url);
      return ['http:', 'https:'].includes(parsed.protocol);
    } catch {
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setUrlError('');

    // Validate URL
    let urlToSubmit = websiteUrl.trim();
    const lowerUrl = urlToSubmit.toLowerCase();
    if (!lowerUrl.startsWith('http://') && !lowerUrl.startsWith('https://')) {
      urlToSubmit = 'https://' + urlToSubmit;
    }

    if (!validateUrl(urlToSubmit)) {
      setUrlError('Please enter a valid website URL');
      return;
    }

    setWebsiteUrl(urlToSubmit);
    trackButtonClick(createRealAgent ? 'create_real_agent' : 'create_demo_agent', analyticsContext);
    setPageState('processing');
    setProcessingStep(0);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 120000);

      // Use different endpoint based on createRealAgent prop
      const endpoint = createRealAgent ? '/api/aiagents/from-website' : '/api/demo-agents';
      
      // Build headers - add auth token for authenticated endpoints
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (createRealAgent) {
        const token = localStorage.getItem('token');
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify({ websiteUrl: urlToSubmit }),
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const text = await response.text();
      let data: { error?: string; code?: string; [key: string]: unknown };
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        setErrorTitle('Something went wrong');
        setErrorMessage(
          response.ok
            ? 'An unexpected error occurred. Please try again.'
            : `Request failed: ${response.status}. ${text.slice(0, 100) || 'Please try again.'}`
        );
        setPageState('error');
        return;
      }

      if (!response.ok) {
        if (data.code === 'CONTENT_BLOCKED') {
          setErrorTitle('Content not allowed');
          setErrorMessage('This website cannot be processed due to content policy.');
        } else if (data.code === 'SPA_OR_LOADING') {
          setErrorTitle("Website can't be read");
          setErrorMessage(
            (data.error as string) ||
            'This website appears to be a single-page app or client-side rendered. We could not extract enough business information.'
          );
        } else {
          setErrorTitle('Something went wrong');
          setErrorMessage((data.error as string) || `Failed to create ${createRealAgent ? 'agent' : 'demo agent'}. Please try again.`);
        }
        setPageState('error');
        return;
      }

      // Handle real agent creation
      if (createRealAgent && onAgentCreated) {
        onAgentCreated(data as unknown as AIAgent);
        // Don't set pageState to success - let parent handle closing modal
        return;
      }

      // Handle demo agent creation
      setDemoAgent(data as unknown as DemoAgent);
      saveDemoToStorage(data as unknown as DemoAgent);
      setPageState('success');
    } catch (error) {
      console.error('Error creating demo agent:', error);
      setErrorTitle('Something went wrong');
      if (error instanceof Error && error.name === 'AbortError') {
        setErrorMessage('The request took too long. Please try again with a simpler website or try again later.');
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
      setPageState('error');
    }
  };

  const handleVoiceChange = (voiceId: string) => {
    if (!demoAgent) return;

    const previousVoice = demoAgent.voiceModel;
    const updated = { ...demoAgent, voiceModel: voiceId };
    setDemoAgent(updated);
    saveDemoToStorage(updated);

    void (async () => {
      try {
        const response = await fetch(`/api/demo-agents/${demoAgent.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ voiceModel: voiceId }),
        });

        if (!response.ok) {
          const reverted = { ...demoAgent, voiceModel: previousVoice };
          setDemoAgent(reverted);
          saveDemoToStorage(reverted);
          console.error('Error updating voice:', response.status);
        }
      } catch (error) {
        const reverted = { ...demoAgent, voiceModel: previousVoice };
        setDemoAgent(reverted);
        saveDemoToStorage(reverted);
        console.error('Error updating voice:', error);
      }
    })();
  };

  const handleReset = () => {
    clearDemoFromStorage();
    setPageState('input');
    setWebsiteUrl('');
    setDemoAgent(null);
    setErrorMessage('');
    setErrorTitle('');
    setProcessingStep(0);
  };

  return (
    <div className="space-y-8">
      {/* Input State */}
      {pageState === 'input' && (
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="website-url" className="block text-lg font-medium text-gray-700 mb-2">
              Your Website URL
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <GlobeAltIcon className="h-6 w-6 text-gray-400" />
              </div>
              <input
                type="text"
                id="website-url"
                value={websiteUrl}
                onChange={(e) => setWebsiteUrl(e.target.value)}
                placeholder="example.com"
                className={`w-full pl-12 pr-4 py-4 text-lg border-2 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  urlError ? 'border-red-500' : 'border-gray-300'
                }`}
              />
            </div>
            {urlError && (
              <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                <ExclamationCircleIcon className="h-4 w-4" />
                {urlError}
              </p>
            )}
          </div>

          <button
            type="submit"
            className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 px-8 rounded-xl text-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            <SparklesIcon className="h-6 w-6" />
            Create My AI Agent
          </button>

          {createRealAgent && onSkipToTemplates && (
            <div className="text-center">
              <button
                type="button"
                onClick={onSkipToTemplates}
                className="text-sm text-gray-600 hover:text-gray-800 underline"
              >
                Skip and use a business template instead
              </button>
            </div>
          )}

          {!createRealAgent && (
            <p className="text-center text-sm text-gray-500">
              We&apos;ll analyze your website and create a personalized AI phone agent.
              <br />
              Demo agents are available for 30 minutes.
            </p>
          )}
          
          {createRealAgent && (
            <p className="text-center text-sm text-gray-500">
              We&apos;ll analyze your website and create a personalized AI phone agent for your business.
            </p>
          )}
        </form>
      )}

      {/* Processing State */}
      {pageState === 'processing' && (
        <div className="space-y-6">
          {showBrowserMockup && (
            <div className="rounded-xl overflow-hidden border-2 border-gray-200 bg-gray-100">
              <ProcessingBrowserMockup websiteUrl={websiteUrl} />
            </div>
          )}

          {/* Horizontal Progress Steps */}
          <div className="py-2">
            {/* Step bubbles + connectors */}
            <div className="flex items-start justify-between">
              {processingSteps.map((step, index) => {
                const Icon = step.icon;
                const isActive = index === processingStep;
                const isComplete = index < processingStep;
                const isLast = index === processingSteps.length - 1;

                return (
                  <Fragment key={index}>
                    <div className="flex flex-col items-center gap-2 flex-shrink-0">
                      <div
                        className={`h-10 w-10 rounded-full flex items-center justify-center transition-all duration-500 ${
                          isActive
                            ? 'bg-blue-500 text-white shadow-md shadow-blue-200 ring-4 ring-blue-100'
                            : isComplete
                            ? 'bg-green-500 text-white'
                            : 'bg-gray-100 text-gray-400'
                        }`}
                      >
                        {isActive ? (
                          <ArrowPathIcon className="h-5 w-5 animate-spin" />
                        ) : isComplete ? (
                          <CheckCircleIcon className="h-5 w-5" />
                        ) : (
                          <Icon className="h-5 w-5" />
                        )}
                      </div>
                      <span
                        className={`text-xs font-medium text-center leading-tight w-16 ${
                          isActive
                            ? 'text-blue-700'
                            : isComplete
                            ? 'text-green-700'
                            : 'text-gray-400'
                        }`}
                      >
                        {step.shortLabel}
                      </span>
                    </div>
                    {!isLast && (
                      <div
                        className={`flex-1 h-0.5 mx-2 mt-5 transition-colors duration-500 ${
                          index < processingStep ? 'bg-green-400' : 'bg-gray-200'
                        }`}
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>

            {/* Active step full label */}
            <div className="mt-5 text-center">
              <p className="inline-flex items-center gap-2 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-100 px-4 py-2 rounded-full">
                <ArrowPathIcon className="h-4 w-4 animate-spin flex-shrink-0" />
                {processingSteps[processingStep]?.label}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Success State */}
      {pageState === 'success' && demoAgent && (
        <div className="space-y-8">
          {/* Success Header */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircleIcon className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Your AI Agent is Ready!
            </h2>
            <p className="text-gray-600">{demoAgent.summary}</p>
            {demoAgent.usedFallback && (
              <p className="mt-3 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 inline-block max-w-xl">
                This demo uses limited context. A full Greet agent gets your full website, FAQs, hours, and scheduling-so it answers more and sounds more like your business. Call the demo below, then sign up to get the full version.
              </p>
            )}
          </div>

          {/* Call Options Card */}
          <div className="bg-gradient-to-r from-green-500 to-blue-500 p-1 rounded-2xl">
            <div className="bg-white p-6 rounded-xl">

              {/* Browser Call (primary) */}
              {demoAgent.vapiAgentId && (
                <div className="text-center mb-5">
                  <p className="text-gray-600 mb-3 font-medium">Talk to your agent right now:</p>

                  {!callState.isCallActive && !callState.isConnecting && (
                    <button
                      onClick={handleWebCall}
                      className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white text-lg font-bold rounded-xl hover:from-green-600 hover:to-blue-600 transition-all shadow-lg hover:shadow-xl"
                    >
                      <MicrophoneIcon className="h-5 w-5" />
                      Call in your browser
                    </button>
                  )}

                  {callState.isConnecting && (
                    <div className="inline-flex items-center gap-3 px-8 py-4 bg-blue-50 border-2 border-blue-200 rounded-xl text-blue-700 font-semibold text-lg">
                      <ArrowPathIcon className="h-5 w-5 animate-spin" />
                      Connecting…
                    </div>
                  )}

                  {callState.isCallActive && (
                    <div className="inline-flex items-center gap-4 px-6 py-3 bg-green-50 border-2 border-green-300 rounded-xl">
                      <span className="flex items-center gap-2 text-green-700 font-semibold">
                        <span className="relative flex h-2.5 w-2.5">
                          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-75" />
                          <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
                        </span>
                        {formatCallDuration(callState.callDuration)}
                      </span>
                      <button
                        onClick={endCall}
                        className="inline-flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-lg transition-colors text-sm"
                      >
                        <StopCircleIcon className="h-4 w-4" />
                        End call
                      </button>
                    </div>
                  )}

                  {callState.error && (
                    <p className="mt-3 text-sm text-red-600 flex items-center justify-center gap-1">
                      <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
                      {callState.error}
                    </p>
                  )}
                </div>
              )}

              {/* Phone Number (secondary / always available) */}
              {demoPhone && (
                <div className={`text-center ${demoAgent.vapiAgentId ? 'border-t pt-4' : ''}`}>
                  {demoAgent.vapiAgentId ? (
                    <p className="text-sm text-gray-500 mb-1">Or call by phone:</p>
                  ) : (
                    <p className="text-gray-600 mb-2">Call this number to test your AI agent:</p>
                  )}
                  <a
                    href={`tel:${demoPhone},${demoAgent.extension}`}
                    className={`block font-black text-gray-900 hover:text-blue-600 transition-colors ${demoAgent.vapiAgentId ? 'text-xl md:text-2xl' : 'text-3xl md:text-4xl'}`}
                  >
                    {formatPhoneNumber(demoPhone)}
                  </a>
                  <p className={`font-bold text-blue-600 mt-1 ${demoAgent.vapiAgentId ? 'text-base' : 'text-2xl mt-2'}`}>
                    Extension: {demoAgent.extension}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-500">
                <ClockIcon className="h-4 w-4" />
                <span>Demo expires in {remainingTime} minutes</span>
              </div>
            </div>
          </div>

          {/* Voice Selector */}
          {showVoiceSelector && (
            <div className="border-t pt-6">
              <VoiceSelector
                selectedVoice={demoAgent.voiceModel || ElevenLabsVoiceId.Will}
                onVoiceChange={handleVoiceChange}
                cardLayout="wide"
              />
            </div>
          )}

          {/* Advanced: full prompt (collapsible) */}
          {showPromptSection && demoAgent.prompt && (
            <div className="border-t pt-6">
              <button
                type="button"
                onClick={() => setShowPromptExpanded((v) => !v)}
                className="w-full flex items-center justify-between px-4 py-3 rounded-xl border-2 border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                aria-expanded={showPromptExpanded}
              >
                <span className="font-medium text-gray-700">Advanced - See Agent Instructions</span>
                {showPromptExpanded ? (
                  <ChevronUpIcon className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />
                ) : (
                  <ChevronDownIcon className="h-5 w-5 text-gray-500 flex-shrink-0" aria-hidden />
                )}
              </button>
              <div
                className={`overflow-hidden transition-all duration-200 ease-out ${
                  showPromptExpanded ? 'max-h-[80vh] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="mt-3 p-4 rounded-xl border-2 border-gray-200 bg-white max-h-[70vh] overflow-y-auto">
                  <div
                    className="prose prose-sm max-w-none text-gray-800 prose-p:text-gray-800 prose-strong:text-gray-900 prose-ul:my-2 prose-ol:my-2 prose-li:my-0.5"
                    dangerouslySetInnerHTML={{ __html: formatPromptForDisplay(demoAgent.prompt) }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* CTA Section */}
          {showSignupCTA && (
            <div className="border-t pt-6">
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-2xl p-6 md:p-8 text-center">
                <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">
                  This is a 30-minute demo
                </p>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Get your full AI agent and your own phone number
                </h3>
                <p className="text-gray-600 mb-6 max-w-lg mx-auto">
                  Save this agent, add your FAQs and hours, get a dedicated number, and keep it forever. No expiration-and it only gets better with customizations.
                </p>
                <div className="flex justify-center">
                  {isAuthenticated ? (
                    <Link
                      href="/app"
                      onClick={() => trackButtonClick('demo_go_dashboard', analyticsContext)}
                      className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors text-lg shadow-lg"
                    >
                      Go to Dashboard
                    </Link>
                  ) : (
                    <Link
                      href="/register"
                      onClick={() => trackButtonClick('demo_signup', analyticsContext)}
                      className="inline-flex items-center justify-center px-8 py-4 bg-blue-600 text-white font-bold rounded-xl hover:bg-blue-700 transition-colors text-lg shadow-lg"
                    >
                      Get my full AI agent - Start free
                    </Link>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Reset button */}
          <div className="text-center">
            <button
              onClick={handleReset}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Create a different demo
            </button>
          </div>
        </div>
      )}

      {/* Error State */}
      {pageState === 'error' && (
        <div className="text-center space-y-6">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full">
            <ExclamationCircleIcon className="h-10 w-10 text-red-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {errorTitle || 'Something went wrong'}
            </h2>
            <p className="text-gray-600">{errorMessage}</p>
          </div>
          <button
            onClick={handleReset}
            className="inline-flex items-center justify-center px-6 py-3 bg-blue-600 text-white font-semibold rounded-xl hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
}

